import { useEffect, useMemo, useRef, useState } from 'react'
import {
  X, Save, Loader2, Trash2, Package, Tag, AlertCircle, Building2, Minus, Plus,
} from 'lucide-react'
import { postJson, patchJson, apiDelete, apiGet } from '../../lib/api.js'
import { ASSET_STATUS, toDateInput, relabelBranch, indexTypes, DEFAULT_USEFUL_LIFE, ACCT_CLASS } from '../../lib/asset.js'
import ExpensePicker from './ExpensePicker.jsx'
import LocationPicker from './LocationPicker.jsx'
import ConfirmDialog from '../ConfirmDialog.jsx'

const FIELD = 'w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-[14px] text-slate-800 placeholder:text-slate-400 outline-none focus:border-brand-400 focus:ring-4 focus:ring-brand-500/10 transition'
const LABEL = 'block text-[12px] font-medium text-slate-500 mb-1'
const STEP = 'w-9 shrink-0 rounded-xl border border-slate-200 bg-slate-50 text-slate-600 flex items-center justify-center transition hover:bg-slate-100 hover:text-slate-800 active:scale-95 disabled:opacity-40 disabled:pointer-events-none'

// อายุการใช้งาน is in whole years, never below 1 (0 would divide by zero in the
// depreciation estimate) and never absurdly long.
const LIFE_MIN = 1
const LIFE_MAX = 50
const clampLife = (v) => Math.min(LIFE_MAX, Math.max(LIFE_MIN, Math.round(Number(v) || DEFAULT_USEFUL_LIFE)))

function Field({ label, children, hint }) {
  return (
    <label className="block">
      <span className={LABEL}>{label}</span>
      {children}
      {hint && <span className="block text-[11px] text-slate-400 mt-0.5">{hint}</span>}
    </label>
  )
}

function SectionHead({ icon: Icon, title }) {
  return (
    <div className="flex items-center gap-2.5 mb-3">
      <span className="w-8 h-8 rounded-lg bg-brand-50 text-brand-600 flex items-center justify-center"><Icon size={16} strokeWidth={2} /></span>
      <h3 className="text-[14.5px] font-semibold text-slate-800">{title}</h3>
    </div>
  )
}

const EMPTY = {
  name: '', brand: '', model: '', serial_num: '',
  asset_type_id: 0, branch_id: 0, date: '', price: '',
  warranty_expire: '', location: '', status: 'active', remark: '',
  expense_id: 0, useful_life: DEFAULT_USEFUL_LIFE, depreciate: true,
}

export default function AssetEditor({ mode, assetId, prefill, types = [], branches = [], onClose, onSaved, onDeleted }) {
  const isEdit = mode === 'edit'
  // Create-from-expense: seed name/price/date + the purchase link from the expense.
  const [form, setForm] = useState(() => {
    if (isEdit || !prefill) return EMPTY
    return {
      ...EMPTY,
      name: prefill.name || '',
      price: prefill.price ?? '',
      date: prefill.date || '',
      expense_id: Number(prefill.expense_id) || 0,
    }
  })
  const [expenseDisplay, setExpenseDisplay] = useState(() => (!isEdit && Number(prefill?.expense_id) > 0) ? `ใบซื้อ #${prefill.expense_id}` : '')
  // Not every asset comes with a warranty — an empty warranty_expire IS "no warranty",
  // this just makes that explicit instead of looking like an unfilled field.
  const [noWarranty, setNoWarranty] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [loading, setLoading] = useState(isEdit)
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [error, setError] = useState('')
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }))

  const { groups } = useMemo(() => indexTypes(types), [types])

  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape' && !saving && !deleting) onClose?.() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose, saving, deleting])

  useEffect(() => {
    if (!isEdit || !assetId) return
    let alive = true
    setLoading(true)
    apiGet(`/api/assets/${assetId}`)
      .then((a) => {
        if (!alive || !a) return
        setForm({
          name: a.name || '', brand: a.brand || '', model: a.model || '', serial_num: a.serial_num || '',
          asset_type_id: a.asset_type_id || 0, branch_id: a.branch_id || 0,
          date: toDateInput(a.date), price: a.price ?? '',
          warranty_expire: toDateInput(a.warranty_expire), location: a.location || '',
          status: a.status || 'active', remark: a.remark || '', expense_id: a.expense_id || 0,
          useful_life: Number(a.useful_life) > 0 ? a.useful_life : DEFAULT_USEFUL_LIFE,
          depreciate: a.depreciate !== false,
        })
        setNoWarranty(!toDateInput(a.warranty_expire))
        if (a.expense_id > 0) setExpenseDisplay(`ใบซื้อ #${a.expense_id}`)
      })
      .catch((e) => { if (alive) setError(String(e.message || e)) })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [isEdit, assetId])

  function buildPayload() {
    const num = (v) => (v === '' || v == null ? null : Number(v))
    return {
      name: form.name.trim(),
      brand: form.brand.trim(),
      model: form.model.trim(),
      serial_num: form.serial_num.trim(),
      asset_type_id: Number(form.asset_type_id) || 0,
      branch_id: Number(form.branch_id) || 0,
      date: form.date || null,
      price: num(form.price),
      warranty_expire: noWarranty ? null : (form.warranty_expire || null),
      location: form.location.trim(),
      status: form.status || 'active',
      remark: form.remark.trim(),
      expense_id: Number(form.expense_id) || 0,
      useful_life: clampLife(form.useful_life),
      depreciate: !!form.depreciate,
    }
  }

  async function save() {
    setError('')
    if (!form.name.trim() && !form.asset_type_id) {
      setError('กรุณากรอกชื่อสินทรัพย์ หรือเลือกประเภทอย่างน้อยหนึ่งอย่าง')
      return
    }
    setSaving(true)
    try {
      const payload = buildPayload()
      let savedId = assetId
      if (isEdit) await patchJson(`/api/assets/${assetId}`, payload)
      else {
        const created = await postJson('/api/assets', payload)
        savedId = created?.id ?? created?.asset?.id ?? 0
      }
      // Keep expense.asset_id in step with asset.expense_id (best-effort reverse
      // link), mirroring AssetMaintenance — so both records point at each other.
      if ((Number(payload.expense_id) || 0) > 0 && savedId) {
        patchJson('/api/expense/' + Number(payload.expense_id), { asset_id: savedId }).catch(() => {})
      }
      onSaved?.()
    } catch (e) {
      setError(String(e.message || e))
    } finally {
      setSaving(false)
    }
  }

  async function remove() {
    if (!isEdit) return
    setConfirmDelete(false)
    setError('')
    setDeleting(true)
    try {
      await apiDelete(`/api/assets/${assetId}`)
      onDeleted?.()
    } catch (e) {
      // 409 from the guard → surface the reason + suggest retiring.
      let msg = String(e.message || e)
      const m = msg.match(/\{.*\}$/)
      if (m) { try { const j = JSON.parse(m[0]); if (j.message) msg = j.message } catch { /* keep raw */ } }
      setError(msg)
    } finally {
      setDeleting(false)
    }
  }

  const busy = saving || deleting

  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-end" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-[2px]" onMouseDown={() => !busy && onClose?.()} />
      <div className="relative w-full max-w-[620px] bg-slate-50 shadow-2xl flex flex-col animate-ss-slide-in">
        {/* header */}
        <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-slate-200 bg-white">
          <div className="flex items-center gap-2.5 min-w-0">
            <span className="w-9 h-9 rounded-xl bg-brand-50 text-brand-600 flex items-center justify-center shrink-0"><Package size={18} strokeWidth={2} /></span>
            <div className="min-w-0">
              <h2 className="text-[16px] font-semibold text-slate-900 truncate">{isEdit ? 'แก้ไขสินทรัพย์' : 'เพิ่มสินทรัพย์'}</h2>
              {isEdit && <p className="text-[12px] text-slate-400">Asset #{assetId}</p>}
            </div>
          </div>
          <button onClick={() => !busy && onClose?.()} className="p-2 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100"><X size={18} /></button>
        </div>

        {/* body */}
        <div className="flex-1 overflow-auto px-5 py-5 space-y-6">
          {loading ? (
            <div className="flex items-center justify-center h-40 text-slate-400"><Loader2 size={20} className="animate-spin mr-2" /> กำลังโหลด…</div>
          ) : (
            <>
              {/* identity */}
              <section className="rounded-2xl border border-slate-200 bg-white p-4">
                <SectionHead icon={Tag} title="ข้อมูลสินทรัพย์" />
                <div className="space-y-3">
                  <Field label="ชื่อสินทรัพย์"><input value={form.name} onChange={set('name')} className={FIELD} placeholder="เช่น เก้าอี้ทำฟัน ห้อง 1" /></Field>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="ยี่ห้อ (Brand)"><input value={form.brand} onChange={set('brand')} className={FIELD} /></Field>
                    <Field label="รุ่น (Model)"><input value={form.model} onChange={set('model')} className={FIELD} /></Field>
                  </div>
                  <Field label="Serial Number"><input value={form.serial_num} onChange={set('serial_num')} className={FIELD} placeholder="หมายเลขเครื่อง" /></Field>
                  <Field label="ประเภท">
                    <select value={form.asset_type_id} onChange={set('asset_type_id')} className={FIELD}>
                      <option value={0}>— เลือกประเภท —</option>
                      {[...groups.entries()].map(([g, rows]) => (
                        <optgroup key={g} label={g}>
                          {rows.map((r) => <option key={r.id} value={r.id}>{r.subtype || r.Type}</option>)}
                        </optgroup>
                      ))}
                    </select>
                  </Field>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="สถานะ">
                      <select value={form.status} onChange={set('status')} className={FIELD}>
                        {ASSET_STATUS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                      </select>
                    </Field>
                    <Field label="สาขา">
                      <select value={form.branch_id} onChange={set('branch_id')} className={FIELD}>
                        <option value={0}>— เลือกสาขา —</option>
                        {branches.map((b) => <option key={b.id} value={b.id}>{relabelBranch(b.name)}</option>)}
                      </select>
                    </Field>
                  </div>
                  <div>
                    <span className={LABEL}>ตำแหน่ง/ที่ตั้งภายในสาขา</span>
                    <LocationPicker
                      value={form.location}
                      branchId={form.branch_id}
                      onChange={(v) => setForm((f) => ({ ...f, location: v }))}
                    />
                    <span className="block text-[11px] text-slate-400 mt-0.5">เลือกจากที่ตั้งที่มีอยู่ หรือพิมพ์เพื่อเพิ่มใหม่ · เช่น ห้องตรวจ 2, เคาน์เตอร์หน้า</span>
                  </div>
                </div>
              </section>

              {/* purchase + lifecycle */}
              <section className="rounded-2xl border border-slate-200 bg-white p-4">
                <SectionHead icon={Building2} title="การจัดซื้อและอายุ" />
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="วันที่ได้มา" hint="ใช้คำนวณอายุสินทรัพย์"><input type="date" value={form.date} onChange={set('date')} className={FIELD} /></Field>
                    <Field label="ราคาซื้อ (บาท)" hint="สำหรับค่าเสื่อมในอนาคต"><input type="number" inputMode="decimal" value={form.price} onChange={set('price')} className={FIELD} placeholder="0" /></Field>
                  </div>
                  {/* Accounting class — drives whether ค่าเสื่อม applies at all, so it
                      sits above the fields it governs. */}
                  <div>
                    <span className={LABEL}>ประเภททางบัญชี</span>
                    <div className="grid grid-cols-2 gap-2">
                      {ACCT_CLASS.map((c) => {
                        const on = !!form.depreciate === c.value
                        return (
                          <button
                            key={String(c.value)} type="button"
                            onClick={() => setForm((f) => ({ ...f, depreciate: c.value }))}
                            className={`rounded-xl border px-3 py-2 text-left transition ${on ? 'border-brand-400 bg-brand-50/60 ring-4 ring-brand-500/10' : 'border-slate-200 bg-white hover:border-slate-300'}`}
                          >
                            <span className={`block text-[13.5px] font-semibold ${on ? 'text-brand-700' : 'text-slate-700'}`}>{c.label}</span>
                            <span className="block text-[11px] text-slate-400 mt-0.5 leading-snug">{c.hint}</span>
                          </button>
                        )
                      })}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <span className={LABEL}>ประกันหมดอายุ (Warranty)</span>
                      <input
                        type="date" value={noWarranty ? '' : form.warranty_expire} onChange={set('warranty_expire')}
                        disabled={noWarranty}
                        className={`${FIELD} ${noWarranty ? 'bg-slate-50 text-slate-400 cursor-not-allowed' : ''}`}
                      />
                      <label className="mt-1.5 inline-flex items-center gap-1.5 text-[12px] text-slate-500 cursor-pointer select-none">
                        <input
                          type="checkbox" checked={noWarranty}
                          onChange={(e) => {
                            const v = e.target.checked
                            setNoWarranty(v)
                            if (v) setForm((f) => ({ ...f, warranty_expire: '' }))
                          }}
                          className="w-3.5 h-3.5 rounded border-slate-300 text-brand-600 focus:ring-2 focus:ring-brand-500/20"
                        />
                        ไม่มีประกัน
                      </label>
                    </div>
                    {!form.depreciate ? (
                      <div>
                        <span className={LABEL}>อายุการใช้งาน (ปี)</span>
                        <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-3 py-2 text-[12.5px] text-slate-400 leading-snug">
                          ไม่ใช้ — ลงเป็นค่าใช้จ่ายทั้งจำนวนแล้ว
                        </div>
                      </div>
                    ) : (
                    <Field label="อายุการใช้งาน (ปี)" hint={`ใช้ประเมินค่าเสื่อม · ปกติ ${DEFAULT_USEFUL_LIFE} ปี`}>
                      <div className="flex items-stretch gap-1.5">
                        <button
                          type="button" aria-label="ลดอายุการใช้งาน" title="ลด 1 ปี"
                          disabled={clampLife(form.useful_life) <= LIFE_MIN}
                          onClick={() => setForm((f) => ({ ...f, useful_life: clampLife(clampLife(f.useful_life) - 1) }))}
                          className={STEP}
                        ><Minus size={15} /></button>
                        <input
                          type="number" inputMode="numeric" min={LIFE_MIN} max={LIFE_MAX}
                          value={form.useful_life}
                          onChange={set('useful_life')}
                          onBlur={() => setForm((f) => ({ ...f, useful_life: clampLife(f.useful_life) }))}
                          className={`${FIELD} text-center tabular-nums`} placeholder={String(DEFAULT_USEFUL_LIFE)}
                        />
                        <button
                          type="button" aria-label="เพิ่มอายุการใช้งาน" title="เพิ่ม 1 ปี"
                          disabled={clampLife(form.useful_life) >= LIFE_MAX}
                          onClick={() => setForm((f) => ({ ...f, useful_life: clampLife(clampLife(f.useful_life) + 1) }))}
                          className={STEP}
                        ><Plus size={15} /></button>
                      </div>
                    </Field>
                    )}
                  </div>
                  <Field label="ผูกกับใบซื้อ (ค่าใช้จ่าย)" hint="เชื่อมกับรายการในหน้า ค่าใช้จ่าย เพื่อดูราคาและเอกสาร">
                    <ExpensePicker
                      value={Number(form.expense_id) || 0}
                      display={expenseDisplay}
                      onPick={(e) => { setForm((f) => ({ ...f, expense_id: e.id })); setExpenseDisplay(`${e.detail || 'ใบซื้อ'} · #${e.id}`) }}
                      onClear={() => { setForm((f) => ({ ...f, expense_id: 0 })); setExpenseDisplay('') }}
                    />
                  </Field>
                </div>
              </section>

              {/* remark */}
              <section className="rounded-2xl border border-slate-200 bg-white p-4">
                <SectionHead icon={Tag} title="หมายเหตุ" />
                <textarea value={form.remark} onChange={set('remark')} rows={3} className={`${FIELD} resize-none`} placeholder="รายละเอียดเพิ่มเติม…" />
              </section>

              {error && (
                <div className="flex items-start gap-2 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2.5 text-[13px] text-rose-700">
                  <AlertCircle size={15} className="shrink-0 mt-0.5" />
                  <span>{error}</span>
                </div>
              )}
            </>
          )}
        </div>

        {/* footer */}
        <div className="flex items-center justify-between gap-2 px-5 py-3.5 border-t border-slate-200 bg-white">
          <div>
            {isEdit && (
              <button
                onClick={() => setConfirmDelete(true)}
                disabled={busy}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-[13.5px] font-medium text-rose-600 hover:bg-rose-50 disabled:opacity-50"
              >
                {deleting ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />} ลบ
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => !busy && onClose?.()} className="px-4 py-2 rounded-lg text-[14px] font-medium text-slate-600 hover:bg-slate-100">ยกเลิก</button>
            <button
              onClick={save}
              disabled={busy || loading}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-[14px] font-semibold text-white bg-brand-600 hover:bg-brand-700 disabled:opacity-50"
            >
              {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} บันทึก
            </button>
          </div>
        </div>
      </div>

      {confirmDelete && (
        <ConfirmDialog
          title="ลบสินทรัพย์นี้ถาวร?"
          message="ลบได้เฉพาะเมื่อไม่มีข้อมูลผูกอยู่ (ใบซื้อ ประวัติตรวจเช็ก ประวัติซ่อม) — ถ้าเลิกใช้แล้วให้เปลี่ยนสถานะเป็น “ปลดระวาง” แทน"
          confirmLabel="ลบถาวร" tone="danger"
          onConfirm={remove}
          onCancel={() => setConfirmDelete(false)}
        />
      )}
    </div>
  )
}
